import { useCallback, useRef, type PointerEvent } from 'react';
import './VerticalResizeHandle.css';

type Props = {
  value: number;
  onChange: (next: number) => void;
  onCommit?: (value: number) => void;
  min?: number;
  max?: number;
  ariaLabel?: string;
  className?: string;
};

export function VerticalResizeHandle({
  value,
  onChange,
  onCommit,
  min = 320,
  max = 2800,
  ariaLabel = '调整区域高度',
  className,
}: Props) {
  const draggingRef = useRef(false);
  const startYRef = useRef(0);
  const startValueRef = useRef(value);
  const valueRef = useRef(value);
  valueRef.current = value;

  const clamp = useCallback(
    (next: number) => Math.min(max, Math.max(min, Math.round(next))),
    [max, min],
  );

  const updateFromPointer = useCallback(
    (clientY: number) => {
      const delta = clientY - startYRef.current;
      onChange(clamp(startValueRef.current + delta));
    },
    [clamp, onChange],
  );

  const onPointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      draggingRef.current = true;
      startYRef.current = event.clientY;
      startValueRef.current = valueRef.current;
      event.currentTarget.setPointerCapture(event.pointerId);
      document.body.classList.add('vertical-resize-dragging');
      updateFromPointer(event.clientY);
    },
    [updateFromPointer],
  );

  const onPointerMove = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (!draggingRef.current) return;
      updateFromPointer(event.clientY);
    },
    [updateFromPointer],
  );

  const endDrag = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      document.body.classList.remove('vertical-resize-dragging');
      onCommit?.(valueRef.current);
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {
        /* ignore */
      }
    },
    [onCommit],
  );

  return (
    <div
      className={['vertical-resize-handle', className].filter(Boolean).join(' ')}
      role="separator"
      aria-orientation="horizontal"
      aria-valuenow={Math.round(value)}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-label={ariaLabel}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    />
  );
}
